// This file is deprecated in favor of @gaia/hooks/useItly.
// Do not instrument new events via trackAnalytics(). Use ampli instead.

import {ampli} from '@gaia/ampli';
import {addListener as addUserListener, getUser} from '@gaia/hooks/useUser';
import type {User} from '@gaia/types';
import * as Sentry from '@sentry/browser';
import {CaptureConsole as CaptureConsoleIntegration} from '@sentry/integrations';
import {BrowserTracing} from '@sentry/tracing';
import amplitude from 'amplitude-js';

declare global {
    interface Window {
        amplitude: typeof amplitude;
    }
}

export function isOptedOut(user?: {isSU: number; webAnalyticsEnabled: boolean}) {
    // Until `user` is a concrete object, we don't know if the user is
    // opted-out, so we assume they are.
    if (user === undefined) return true;

    if (user.isSU === 1) return true;

    // `user.webAnalyticsEnabled` is statically `true` for anonymous users
    return !user.webAnalyticsEnabled;
}

/**
 * Set or update the analytics user profiles for Sentry, Braze (AppBoy), and
 * Amplitude. Use a promise chain so it handles its own exceptions (otherwise,
 * calling code needs to chain a `catch`).
 */
function updateAnalyticsUser(user: User) {
    // this is called at the end of the `analyticsInitialized` promise, and
    // then only after changes to the `user`, so we can safeily assume that a
    // nalytics is initialized by now

    if (isOptedOut(user)) return;

    // Should be `undefined`, not `"null"`, see:
    // https://www.docs.developers.amplitude.com/data/sdks/typescript-ampli/#identify
    ampli.identify(user.isAuthenticated ? `${user.id}` : undefined);

    if (DEPLOYMENT !== 'development') {
        // likewise, Sentry users are anonymous by default, and `setUser` should be called with
        // properties that "uniquely identify the user." For anonymous users, `id` is `null` and
        // `email` is an empty string, neither of which (or in combination) is unique.
        // https://docs.sentry.io/platforms/javascript/enriching-events/identify-user/
        if (user.isAuthenticated) {
            Sentry.setUser({
                id: `${user.id}`,
                email: user.email,
            });
        }
    }
}

if (DEPLOYMENT !== 'development') {
    Sentry.init({
        dsn: 'https://eebe259ebaa846d39aaae0e3404505ab@o166208.ingest.sentry.io/1238795',
        release: COMMIT_HASH,
        environment: DEPLOYMENT,
        tracesSampleRate: 0.1,
        integrations: [
            // https://docs.sentry.io/platforms/javascript/performance/instrumentation/automatic-instrumentation/#tracingorigins
            new BrowserTracing({
                tracingOrigins: [
                    'localhost',
                    /^\/(?!api\/user\/v2\/profile\/$)/, // negative lookahead to exclude /api/user/v2/profile/
                ],
            }),
            new CaptureConsoleIntegration({levels: ['error']}),
        ],
        ignoreErrors: [
            'Style is not done loading',
            // tile request failed, tile is missing, or request was canceled
            'Failed to fetch',
            'Fetch is aborted',
            'NetworkError when attempting to fetch resource',
            // Tile 404 responses often don't include an Access-Control-Allow-Origin header
            'Origin https://www.gaiagps.com is not allowed by Access-Control-Allow-Origin',
            // Bug related to a bot https://forum.sentry.io/t/unhandledrejection-non-error-promise-rejection-captured-with-value/14062/25
            'Non-Error promise rejection captured',
            // Ignore errors from Safari extensions
            '@safari-extension',
            // If a user clicks the browser cancel button while the page is loading this is thrown
            'TypeError: cancelled',
            // Cookies are disabled
            'SecurityError: The operation is insecure',
            // Firefox bug - https://stackoverflow.com/questions/66033349/javascript-error-on-firefox-typeerror-cant-redefine-non-configurable-property
            'can\'t redefine non-configurable property "userAgent"',
        ],
    });
}

export const analyticsInitialized = (async () => {
    const user = await getUser();

    if (isOptedOut(user)) return;

    // initialize and configure the amplitude SDK
    ampli.load({
        environment: DEPLOYMENT === 'production' ? 'production' : 'development',
        client: {
            options: {
                includeReferrer: true,
                includeUtm: true,
                includeGclid: true,
                deviceId: user.amplitudeDeviceId,
                disableCookies: true,
            },
        },
    });

    // Store referrer & landing page
    if (
        !user.isAuthenticated &&
        document.cookie.search('referrer') < 0 &&
        document.cookie.search('landing_page') < 0
    ) {
        // Store referrer only the first time a user hits the website + landing page
        document.cookie = 'referrer=' + document.referrer + ';path=/';
        document.cookie = 'landing_page=' + location.href + ';path=/';
        ampli.setLandingPage({page: location.href});
    }

    // This is exclusively for the legacy angular code. Do not use otherwise.
    window.amplitude = amplitude;

    // update the analytics user profile, and subscribe to future user updates
    // to keep it up to date
    updateAnalyticsUser(user);
    addUserListener(() => getUser().then(updateAnalyticsUser));
})().catch(console.error);

export default function trackAnalytics<T extends AnalyticsEvent>(
    name: T['analyticsEventName'],
    properties?: Omit<T, 'analyticsEventName'>
) {
    analyticsInitialized
        .then(getUser)
        .then((user) => {
            if (isOptedOut(user)) return;

            // Amplitude complains about empty label property
            if (
                properties &&
                ((properties as any).label === null || (properties as any).label === '')
            ) {
                delete (properties as any)['label'];
            }

            amplitude.getInstance().logEvent(name, properties);
        })
        .catch(console.warn);
}

export function trackFooterClick(category: string, name: string) {
    trackAnalytics('Clicked Footer Link', {category, name});
}

export function trackAppStoreClick(platform: 'iOS' | 'Android') {
    const event = platform === 'iOS' ? 'Select Download iOS App' : 'Select Download Android App';
    trackAnalytics(event, {category: 'Commerce', label: 'Home Page'});
}

const proxiedAmpli = new Proxy(ampli, {
    get(target, prop, receiver) {
        return (...ampliArgs: any[]) => {
            analyticsInitialized
                .then(getUser)
                .then((user) => {
                    if (isOptedOut(user)) return;

                    const send = Reflect.get(target, prop, receiver);

                    if (typeof send !== 'function') {
                        throw new Error(`Ampli function does not exist: ${prop.toString()}`);
                    }

                    send.apply(ampli, ampliArgs);
                })
                .catch(console.error);
        };
    },
});

export {proxiedAmpli as ampli};
